import React from 'react';
import {
  OperationByMileageOrTimeList,
  OperationByServiceList,
} from '@1po/1po-bff-fe-spec/generated/catalog/maintenance_plan/response/GetMaintenancePlanResponse';
import { TFunction } from 'i18next';
import { CheckCircleIcon, ClockFullIcon, DotIcon, ExclamationCircleIcon, QuestionCircleIcon } from 'assets/icons';
import { convertMileageToCurrentUnits } from 'domains/maintenancePlan/MaintenancePlan.mapper';
import { mileage_units } from 'domains/maintenancePlan/MaintenancePlan.types';
import { RotatedText } from 'pages/CatalogPage/DH/Maintenance/Maintenance.styled';
import { theme } from 'styles';
import { Box, CenterFlex, Flex, Icon, Text } from 'UI';

export interface OperationColumnType extends OperationByMileageOrTimeList {
  status1: string;
  status: number;
  nextMileage: number;
  nextMileageUnit: string;
  operationYears: string[];
}

const Title = ({ children }: { children: string }) => (
  <Text type="lg" bold disableGutter transform={'sentence-case'}>
    {children}
  </Text>
);

const RotatedTitle = ({ children }: { children: string }) => {
  return (
    <RotatedText type={'text_dim'} disableGutter>
      {children}
    </RotatedText>
  );
};

export const getUnitsName = (t: TFunction, units: string): string =>
  units === 'km' ? t('catalog.maintenance.units_km', 'Km') : t('catalog.maintenance.units_miles', 'Miles');

const renderOperationStatusIcon = (status: number | undefined) => {
  const icon = getIcon(status);
  const color = getColor(status);
  return <>{icon && <Icon IconComponent={icon} color={color} background={'transparent'} noPointer size={24} />}</>;
};

const getIcon = (status: number | undefined) => {
  switch (status) {
    case 0:
      return CheckCircleIcon;
    case 1:
      return ExclamationCircleIcon;
    case 2:
      return ClockFullIcon;
    default:
      return QuestionCircleIcon;
  }
};

const getColor = (status: number | undefined) => {
  switch (status) {
    case 0:
      return theme.color.selected;
    case 1:
      return theme.color.warning;
    case 2:
      return theme.color.error;
    default:
      return theme.color.white;
  }
};

const getTitle = (t: TFunction, status: number | undefined) => {
  switch (status) {
    case 0:
      return t('catalog.maintenance.state.operation_updated', 'Upcoming operations');
    case 1:
      return t('catalog.maintenance.state.operation_todo', 'Operations to be carried out currently');
    case 2:
      return t('catalog.maintenance.state.operation_to_catchup', 'Operations not carried out or delayed');
    default:
      return t('catalog.maintenance.state.unknown_state', 'Unknown state');
  }
};

export const getMileageSectionColumns = (
  getLabel: (id: number) => string,
  t: TFunction,
  unitsKm: mileage_units,
  hoveredColumn: string | null,
  setHoveredColumn: React.Dispatch<React.SetStateAction<string | null>>,
) => {
  const unitsKey = unitsKm === 'km' ? 'everyKm' : 'everyMiles';
  return [
    {
      title: <Text type="base">{getLabel(26)}</Text>,
      dataIndex: 'label',
      key: 'label',
      render: (label: number, record: OperationColumnType) => (
        <span
          className={
            hoveredColumn && record.operationYears.includes(hoveredColumn) ? 'highlight-operation-label-cell' : ''
          }
        >
          {label}
        </span>
      ),
    },
    {
      title: <Title>{`${getLabel(40)} (${getUnitsName(t, unitsKm)})`}</Title>,
      dataIndex: unitsKey,
      key: unitsKey,
      width: 150,
      align: 'center' as const,
      render: (everyMiles: number) => {
        return <CenterFlex>{everyMiles || '.'}</CenterFlex>;
      },
    },
    {
      title: (
        <Text type="lg" bold disableGutter>
          {getLabel(42)}
        </Text>
      ),
      key: 'atYear',
      children: Array.from({ length: 12 }).map((_, index) => {
        const yearIndex = index + 1;
        const setHover = () => ({
          onMouseEnter: () => setHoveredColumn(`year_${yearIndex}`),
          onMouseLeave: () => setHoveredColumn(null),
          className: hoveredColumn === `year_${yearIndex}` ? 'highlight-operation-year-column' : '',
        });
        return {
          title: <RotatedTitle>{`${yearIndex} ${index ? getLabel(10) : getLabel(9)}`}</RotatedTitle>,
          className: hoveredColumn === `year_${yearIndex}` ? 'highlight-operation-year-column' : '',
          dataIndex: 'everyYears',
          key: `year_${yearIndex}`,
          align: 'left' as const,
          render: (_value: string, record: OperationColumnType) => {
            return (
              <CenterFlex>
                {record.operationYears.includes(`year_${yearIndex}`) && (
                  <Box align={'center'}>
                    <Icon IconComponent={DotIcon} size={8} noPointer />
                  </Box>
                )}
              </CenterFlex>
            );
          },
          onCell: setHover,
          onHeaderCell: setHover,
        };
      }),
    },
  ];
};

export const getMileageOperationColumns = (t: TFunction, unitsKm: mileage_units, status?: number) => {
  return [
    {
      title: () => (
        <Flex direction={'row'} align={'center'} justify={'center'} gap={10}>
          <Title>{getTitle(t, status)}</Title>
          {renderOperationStatusIcon(status)}
        </Flex>
      ),
      dataIndex: 'label',
      key: 'label',
    },
    ...(status !== 2
      ? [
          {
            title: <Title>{getUnitsName(t, unitsKm)}</Title>,
            dataIndex: 'nextMileage',
            key: 'nextMileage',
            width: 200,
            align: 'center' as const,
            render: (mileage: number, record: OperationColumnType) => {
              return (
                <CenterFlex>{convertMileageToCurrentUnits(unitsKm, record.nextMileageUnit, mileage) || '.'}</CenterFlex>
              );
            },
          },
        ]
      : []),
  ];
};

export const getServiceColumns = (
  allServices: number[],
  operationByMileageOrTimeList: OperationByMileageOrTimeList[],
  printView = false,
) => {
  const servicesColumns = () => {
    if (allServices.length > 0) {
      return allServices.map((serviceColumnNumber) => {
        const serviceData = operationByMileageOrTimeList.find((o) => o.id === serviceColumnNumber);
        return {
          title: (
            <Text type="lg" bold disableGutter>
              {serviceData?.label ?? ''}
            </Text>
          ),
          key: serviceColumnNumber,
          width: printView ? undefined : 120,
          render: (service: OperationByServiceList) => {
            return (
              <CenterFlex>
                {service.carriedOutByOperationByMileageOrTimeIdList.includes(serviceColumnNumber) && (
                  <Box align={'center'}>
                    <Icon IconComponent={DotIcon} size={8} noPointer />
                  </Box>
                )}
              </CenterFlex>
            );
          },
        };
      });
    } else {
      return [
        {
          dataIndex: 'empty',
          key: 'empty',
          width: printView ? undefined : 120,
          align: 'center' as const,
          render: () => (
            <Box align={'center'}>
              <Icon IconComponent={DotIcon} size={8} noPointer />
            </Box>
          ),
        },
      ];
    }
  };

  return [
    {
      dataIndex: 'label',
      key: 'label',
      render: (label: string) => {
        return (
          <Flex minHeight={30} align={'center'}>
            {label}
          </Flex>
        );
      },
    },
    ...servicesColumns(),
  ];
};
